import React, { useState } from 'react';
import SalesOverviewService, { InvoiceMonthHistory } from '../services/SalesOverviewService';
import { CHART_COLORS, CHART_SEMANTIC } from '../../../utils/chartColors';

interface RevenueMonthlyBarChartProps {
  data: InvoiceMonthHistory[];
}

function formatMonthLabel(month: string): string {
  const [year, m] = month.split('-');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthIndex = parseInt(m, 10) - 1;
  return `${monthNames[monthIndex]} ${year.slice(2)}`;
}

export default function RevenueMonthlyBarChart({ data }: RevenueMonthlyBarChartProps): React.JSX.Element {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  if (data.length === 0) {
    return (
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px 24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}
      >
        <div style={{ fontSize: '16px', fontWeight: 600, color: '#021a4a', marginBottom: '16px' }}>
          Monthly Performance
        </div>
        <div
          style={{
            padding: '40px',
            textAlign: 'center',
            color: '#94a3b8',
            fontSize: '14px',
            fontStyle: 'italic',
          }}
        >
          No invoice data available yet
        </div>
      </div>
    );
  }

  const displayData = data.slice(-6);

  const maxValue = Math.max(
    ...displayData.map((p) => Math.max(p.invoicedGross, p.paidGross)),
    1
  );

  const barWidth = 24;
  const gap = 8;
  const groupWidth = barWidth * 2 + gap;
  const chartHeight = 160;
  const labelHeight = 24;

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '12px',
        padding: '20px 24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      }}
    >
      <div style={{ fontSize: '16px', fontWeight: 600, color: '#021a4a', marginBottom: '16px' }}>
        Monthly Performance
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '24px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: CHART_SEMANTIC.invoiced }} />
          <span style={{ fontSize: '12px', color: '#666' }}>Invoiced</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: CHART_SEMANTIC.paid }} />
          <span style={{ fontSize: '12px', color: '#666' }}>Paid</span>
        </div>
      </div>

      {/* Chart */}
      <div style={{ position: 'relative' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'flex-end',
            height: chartHeight,
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          {displayData.map((point, index) => {
            const invoicedHeight = (point.invoicedGross / maxValue) * (chartHeight - 10);
            const paidHeight = (point.paidGross / maxValue) * (chartHeight - 10);
            const label = formatMonthLabel(point.month);

            return (
              <div
                key={point.month}
                style={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: `${gap}px`,
                  position: 'relative',
                }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {/* Invoiced bar */}
                <div
                  style={{
                    width: `${barWidth}px`,
                    height: `${Math.max(invoicedHeight, 2)}px`,
                    background: CHART_SEMANTIC.invoiced,
                    borderRadius: '4px 4px 0 0',
                    transition: 'height 0.3s ease',
                  }}
                />
                {/* Paid bar */}
                <div
                  style={{
                    width: `${barWidth}px`,
                    height: `${Math.max(paidHeight, 2)}px`,
                    background: CHART_SEMANTIC.paid,
                    borderRadius: '4px 4px 0 0',
                    transition: 'height 0.3s ease',
                  }}
                />

                {/* Tooltip */}
                {hoveredIndex === index && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: Math.max(invoicedHeight, paidHeight) + 10,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: '#1e293b',
                      color: 'white',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      zIndex: 10,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>{label}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: CHART_SEMANTIC.invoiced }} />
                      <span>Invoiced: {SalesOverviewService.formatCurrency(point.invoicedGross)}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: CHART_SEMANTIC.paid }} />
                      <span>Paid: {SalesOverviewService.formatCurrency(point.paidGross)}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: CHART_SEMANTIC.open }} />
                      <span>Open: {SalesOverviewService.formatCurrency(point.openGross)}</span>
                    </div>
                    {/* Tooltip arrow */}
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '-6px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 0,
                        height: 0,
                        borderLeft: '6px solid transparent',
                        borderRight: '6px solid transparent',
                        borderTop: '6px solid #1e293b',
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* X-axis labels */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            height: `${labelHeight}px`,
            paddingTop: '8px',
          }}
        >
          {displayData.map((point) => (
            <div
              key={point.month}
              style={{
                width: `${groupWidth}px`,
                textAlign: 'center',
                fontSize: '11px',
                color: '#666',
              }}
            >
              {formatMonthLabel(point.month)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
